import React from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@tiny/components";
import { TypographyProps } from "@tiny/components/src/Typography/Typography.types";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import CloseBig from "@tiny/icons/generated/CloseBig";
import Info2Outline from "@tiny/icons/generated/Info2Outline";
import {
  formatNumber,
  formatPercent,
} from "@tiny/components/src/utils/formatUtils";
import { useSegmentSummary } from "./SegmentSummary.hooks";

export interface SegmentSummaryProps {
  filter?: any;
  hideRefresh?: boolean;
  noBorderRadius?: boolean;
  onClose?: () => void;
  refreshDisabled?: boolean;
  refreshDisabledTooltip?: string;
  isFilterValid?: boolean;
  segmentId?: string;
  onRefreshClick?: () => void;
  skipInitialSummaryFetch?: Boolean;
}

function SegmentSummary(props: SegmentSummaryProps) {
  const {
    refresh,
    countCustomers,
    countNetCustomers,
    clickedPercentage,
    openedPercentage,
    unsubscribedPercentage,
    audiencesIn,
    audiencesNotIn,
    refreshing,
    hideRefresh,
    description,
    noBorderRadius,
    onClose,
    refreshDisabled,
    refreshDisabledTooltip,
    isFilterValid,
    isLoading,
    isps,
  } = useSegmentSummary(props);

  if (isLoading) {
    return (
      <Box
        borderRadius={noBorderRadius ? "0px" : "8px"}
        height="300px"
        component="section"
        bgcolor="yellow.lighter"
        p={4}
        position="relative"
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex="1 1 0"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      borderRadius={noBorderRadius ? "0px" : "8px"}
      component="section"
      bgcolor="yellow.lighter"
      p={4}
      position="relative"
    >
      <Title>Segment Summary</Title>
      <Box position="absolute" top="16px" right="16px">
        {onClose ? (
          <IconButton size="small" onClick={onClose} color="inherit">
            <CloseBig />
          </IconButton>
        ) : (
          <Tooltip title="Total number of customers from the audience who qualify based on the criteria. Subscribers exclude the customers who have unsubscribed, bounced or marked as spam.">
            <IconButton size="tiny" color="primary">
              <Info2Outline />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Body>{formatNumber(countCustomers)} total customers</Body>
      <Body>{formatNumber(countNetCustomers)} subscribers</Body>
      <Subtitle>Audience</Subtitle>
      {audiencesIn.length === 0 && audiencesNotIn.length === 0 && (
        <Body>All Customers</Body>
      )}
      {audiencesIn.map((audience) => (
        <Body key={audience.name}>
          {`In ${audience.name} (${formatNumber(
            audience.totalSubscribers
          )} customers)`}
        </Body>
      ))}
      {audiencesNotIn.map((audience) => (
        <Body key={audience.name}>
          {`Not in ${audience.name} (${formatNumber(
            audience.totalSubscribers
          )} customers)`}
        </Body>
      ))}
      <Subtitle>Your applied filters</Subtitle>
      <Body wordBreak="break-word">{description}</Body>
      <Subtitle mb={2}>Your segment breakdown</Subtitle>
      <LinearProgress variant="determinate" value={openedPercentage} />
      <Body mt={1} mb={2}>
        {formatPercent(openedPercentage)} have opened before
      </Body>
      <LinearProgress variant="determinate" value={clickedPercentage} />
      <Body mt={1} mb={2}>
        {formatPercent(clickedPercentage)} have clicked before
      </Body>
      <LinearProgress variant="determinate" value={unsubscribedPercentage} />
      <Body mt={1}>
        {formatPercent(unsubscribedPercentage)} have unsubscribed
      </Body>
      <Subtitle>ISP Breakdown</Subtitle>
      <Box
        style={{
          maxHeight: "200px",
          overflow: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {isps?.map((isp) => {
          return (
            <Tooltip
              title={`Opens: ${
                isp?.uniqueOpenCount?.toLocaleString() || 0
              }  | Clicks: ${
                isp?.uniqueClickCount?.toLocaleString() || 0
              } | Subscribers: ${isp?.subscribedCount?.toLocaleString() || 0}`}
              customContent
              key={isp?.ispName}
              arrow
            >
              <Box
                mt={2}
                display={"flex"}
                width={"100%"}
                justifyContent={"space-between"}
                p={2}
                bgcolor={"white"}
              >
                <Body>{isp?.ispName}</Body>
                <Body>{isp?.subscribedCount?.toLocaleString() || 0}</Body>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      {!hideRefresh && (
        <Box mt={4}>
          <Button
            variant="outlined"
            fullWidth
            onClick={refresh}
            disabled={refreshing || refreshDisabled || !isFilterValid}
            loading={refreshing}
            disabledTooltip={!refreshing ? refreshDisabledTooltip : undefined}
          >
            Refresh Summary
          </Button>
        </Box>
      )}
    </Box>
  );
}

function Title(props: Partial<TypographyProps>) {
  const { children, ...other } = props;
  const { mdUp } = useBreakpoints();
  return (
    <Typography
      {...other}
      variant={mdUp ? "h4" : "h3"}
      color="grey"
      tone={900}
      fontWeight="semiBold"
      m={0}
    >
      {children}
    </Typography>
  );
}

function Subtitle(props: Partial<TypographyProps>) {
  const { children, ...other } = props;
  const { mdUp } = useBreakpoints();
  return (
    <Typography
      {...other}
      variant={mdUp ? "label2" : "label1"}
      color="grey"
      tone={900}
      fontWeight="bold"
      display="block"
      mt={4}
    >
      {children}
    </Typography>
  );
}

function Body(props: Partial<TypographyProps>) {
  const { children, wordBreak = "break-all", ...other } = props;
  const { mdUp } = useBreakpoints();
  return (
    <Typography
      {...other}
      variant={mdUp ? "body3" : "body2"}
      color="grey"
      tone={700}
      display="block"
      wordBreak={wordBreak}
    >
      {children}
    </Typography>
  );
}

export default React.memo(SegmentSummary);
